import { Link, useLocation } from "react-router-dom";
import { dataSideBar, dataSideBarCustomerArea } from "../../data/dataSideBar";
import { useTimeStore } from "../../stores/time";
import { useCrmStore } from "../../stores/crm";
import { useButtonStore } from "../../stores/components/button";
import { useUserContext } from "../../contexts/userContext";
import { useStaffStore } from "../../stores/staff";
import { useState } from "react";
import { useInvoiceStore } from "../../stores/invoice";
import { useSocialStore } from "../../stores/social";
import { useUtilsStore } from "../../stores/components/utils";
import { usePaymentStore } from "../../stores/payment";
import { useNavStore } from "../../stores/components/nav";

const Sidebar = () => {
  const { resetCrm, setCurrentPage } = useCrmStore();
  const { resetButton } = useButtonStore();
  const { resetTime } = useTimeStore();
  const { resetStaff } = useStaffStore();
  const { resetPayment } = usePaymentStore();
  const { resetInvoice } = useInvoiceStore();
  const { resetSocial } = useSocialStore();
  const { setSearchTerm } = useUtilsStore();
  const { role, departments, billingContact, sc_UserToCustomerFile } = useUserContext();
  const [tooltipItemId, setTooltipItemId] = useState<number | null>(null);
  const { isMobileNavOpen, setMobileNavOpen } = useNavStore();

  const location = useLocation();
  const isCustomerArea = location.pathname.includes("customer-area");

  const isItemActive = (link: string) => {
    const normalize = (v: string) => (v.endsWith("/") ? v.slice(0, -1) : v);
    const normalizedLink = normalize(link);
    const normalizedPath = normalize(location.pathname);

    return (
      normalizedPath === normalizedLink ||
      normalizedPath.startsWith(`${normalizedLink}/`)
    );
  };

  const dataMenuFilter = isCustomerArea
    ? dataSideBarCustomerArea.filter((item) => {
      // Toujours retourner Dashboard
      if (item.title === "Dashboard") {
        return true;
      }
      
      // Vérifier les permissions pour Social
      if (item.title === "Social") {
        return sc_UserToCustomerFile.length > 0;
      }
      
      // Vérifier les permissions pour Factures
      if (item.title === "Factures") {
        return billingContact === true;
      }
      
      // Par défaut, ne pas afficher les autres éléments
      return false;
    })
    : dataSideBar.filter((item) => {
    // Si l'utilisateur a le département SOCIAL ou est Admin/Direction, il peut voir la brique Social
    if (item.title === "Social") {
      return departments.includes("SOCIAL") || role === "Admin" || role === "Direction";
    }
      
      // Filtre par rôle pour les autres briques
      if (role === "Lecteur") {
        return (
          item.title !== "Trésorerie" &&
          item.title !== "CRM" &&
          item.title !== "Staff" &&
          item.title !== "Paramètres"
        );
      } else if (role === "Editeur" || role === "Superviseur") {
        return (
          item.title !== "Trésorerie" &&
          item.title !== "CRM" &&
          item.title !== "Staff" &&
          item.title !== "Paramètres"
        );
      } else if (role === "Comptabilité") {
        return (
          item.title !== "CRM" &&
          item.title !== "Staff" &&
          item.title !== "Paramètres"
        );
      } else if (role === "Admin") {
        return true;
      } else if (role === "Direction") {
        return true;
      }
      
      // Par défaut, retourne tous les éléments
      return true;
    });

  const handleReset = () => {
    setSearchTerm("");
    
    // Réinitialiser tous les stores
    resetCrm();
    resetInvoice();
    resetStaff();
    resetTime();
    resetPayment();
    resetSocial();
    resetButton();
    setCurrentPage(1);
  };

  return (
    <>
      {/* Rail d'icônes — à partir de la tablette */}
      <nav
        aria-label="Navigation principale"
        className="fixed top-16 left-0 z-40 hidden h-[calc(100vh-4rem)] w-24 border-r border-slate-200 bg-white/90 px-4 py-4 backdrop-blur md:block"
      >
        <div className="flex flex-col items-center gap-2">
          {dataMenuFilter?.map((item) => {
            const isActive = isItemActive(item.link);

            return (
              <Link
                key={item.id}
                to={item.link}
                onClick={() => handleReset()}
                aria-label={item.title}
                aria-current={isActive ? "page" : undefined}
                aria-describedby={
                  tooltipItemId === item.id
                    ? `sidebar-tooltip-${item.id}`
                    : undefined
                }
                onMouseEnter={() => setTooltipItemId(item.id)}
                onMouseLeave={() => setTooltipItemId(null)}
                onFocus={() => setTooltipItemId(item.id)}
                onBlur={() => setTooltipItemId(null)}
                className={`group relative flex size-14 items-center justify-center rounded-xl border transition-colors focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none ${
                  isActive
                    ? "border-indigo-200 bg-indigo-50"
                    : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                }`}
              >
                <img
                  src={item.icon.replace("whiteIcons", "darkIcons")}
                  alt=""
                  aria-hidden="true"
                  className={`size-6 transition-opacity ${
                    isActive ? "opacity-100" : "opacity-60 group-hover:opacity-100"
                  }`}
                />

                {/* Repère de page active */}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute top-1/2 -left-4 h-7 w-1 -translate-y-1/2 rounded-r-full bg-dca-blue"
                  />
                )}

                {/* Infobulle du titre */}
                {tooltipItemId === item.id && (
                  <span
                    id={`sidebar-tooltip-${item.id}`}
                    role="tooltip"
                    className="pointer-events-none absolute left-full z-50 ml-3 rounded-lg bg-slate-800 px-2.5 py-1.5 font-Nunito text-xs font-semibold whitespace-nowrap text-white shadow-lg"
                  >
                    {item.title}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Tiroir de navigation — mobile */}
      {isMobileNavOpen && (
        <div className="fixed inset-0 z-60 md:hidden">
          <button
            type="button"
            aria-label="Fermer le menu"
            onClick={() => setMobileNavOpen(false)}
            className="absolute inset-0 cursor-pointer bg-slate-900/40 backdrop-blur-sm"
          />

          <nav
            aria-label="Navigation principale"
            className="animate-fade-in absolute top-0 left-0 flex h-full w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-xl"
          >
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 px-4">
              <span className="font-Nunito text-sm font-bold tracking-wider text-slate-400 uppercase">
                Navigation
              </span>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Fermer le menu"
                className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"
              >
                <svg
                  aria-hidden="true"
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              <div className="flex flex-col gap-1">
                {dataMenuFilter?.map((item) => {
                  const isActive = isItemActive(item.link);

                  return (
                    <Link
                      key={item.id}
                      to={item.link}
                      onClick={() => {
                        handleReset();
                        setMobileNavOpen(false);
                      }}
                      aria-current={isActive ? "page" : undefined}
                      className={`flex items-center gap-3 rounded-xl px-3 py-3 font-Nunito text-sm font-semibold transition-colors ${
                        isActive
                          ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 ring-inset"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <img
                        src={item.icon.replace("whiteIcons", "darkIcons")}
                        alt=""
                        aria-hidden="true"
                        className="size-5 shrink-0 opacity-70"
                      />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Sidebar;
